/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import dashboardActions from '@store/actions/dashboard';
import layoutConfig from '../modules/layoutConfig';
import { setSelectedProp } from '../modules/dialogGraphEvents/selectProp';
import { searchNextStep } from '../modules/dialogGraphEvents/nextStepProp';
import { searchPrevStep } from '../modules/dialogGraphEvents/prevStepProp';
import { setArrowsProps } from '../modules/dialogGraphEvents/setArrowProps';
import checkNodeType from '../modules/dialogGraphEvents/checkNodeType';
import { updateLabelsPositions } from '../modules/dialogGraphEvents/updateLabelsPositions';
import { props, onFitProps } from '../modules/constants';
import createLabels from '../modules/dialogGraphEvents/createLabels';
import changeBgLabel from '../modules/dialogGraphEvents/changeBgLabel';
import setPropToAllNodes from '../modules/dialogGraphEvents/set-prop-to-all-nodes';
import isNodesOutOfView from '../modules/dialogGraphEvents/isNodesOutOfView';
import resetStatus from '../modules/dialogGraphEvents/resetStatus';
import getNodesViewport from '../modules/dialogGraphEvents/getNodesViewport';

const dialogGraphEvents = async (cy, dispatch, graphPositionsUpdated, setHoverNode, setZoomCy) => {
  let popperRef; // instance
  const tooltipNodeEle = document.getElementById('tooltipNode');

  const onInit = async () => {
    cy.elements().not(':hidden').unlock();
    dispatch(dashboardActions.setGraphPositionsUpdated(true));

    requestAnimationFrame(() => {
      Object.keys(props.state).forEach(prop => {
        setPropToAllNodes({ prop, value: false }, cy);
      });
      createLabels(cy);
      cy.elements().not(':hidden').layout(layoutConfig).run();

      cy.ready(() => {
        setTimeout(() => {
          cy.fit(cy.elements().not(':hidden'), onFitProps.margin.onInit);
          setZoomCy(cy.zoom());
        }, 500);
      });
    });
  };

  if (graphPositionsUpdated.data !== true) onInit();

  cy.on('select', 'node', evt => {
    const node = evt.target;
    const isParent = checkNodeType({ node, typeToCheck: props.type.parent });

    if (!isParent) {
      changeBgLabel(node);
      setSelectedProp(cy, node);
      searchNextStep(cy, node);
      searchPrevStep(cy, node);
      setArrowsProps(cy, node);
    }
  });

  cy.on('drag', 'node', async evt => {
    const node = evt.target;

    const limitNodeToViewport = () => {
      const isOutsideCanvas = getNodesViewport({ mode: 'outside', node }, cy);
      if (isOutsideCanvas) {
        const positionOutside = isNodesOutOfView(cy, { margin: -50 }, node, true);

        if (positionOutside.top) node.shift({ y: 15 });
        if (positionOutside.bottom) node.shift({ y: -15 });
        if (positionOutside.right) node.shift({ x: -15 });
        if (positionOutside.left) node.shift({ x: 15 });
        node.ungrabify();
      }
    };

    limitNodeToViewport();

    const isParent = checkNodeType({ node, typeToCheck: props.type.parent });

    if (isParent) {
      const blockMovementForEmptyParentsNodes = () => {
        const blockmovement = node
          .children()
          .every(ele => checkNodeType({ node: ele, typeToCheck: props.type.fallback }));
        if (blockmovement) node.ungrabify();
      };

      blockMovementForEmptyParentsNodes();
    }

    requestAnimationFrame(async () => {
      updateLabelsPositions(cy);
      if (!isParent && popperRef) {
        await popperRef.update();
      }
    });
  });

  cy.on('dragpan', () => {
    updateLabelsPositions(cy);

    const AllNodesInCanvas = cy.nodes().not(':hidden');

    let nodesInside = 0;
    for (let i = 0; i < AllNodesInCanvas.length; i++) {
      const node = AllNodesInCanvas[i];
      const nodeStillInside = getNodesViewport({ mode: 'inside', node }, cy);
      if (nodeStillInside) {
        nodesInside++;
        break;
      }
    }

    if (nodesInside === 0) resetStatus(cy);
  });

  // method one better than on, avoid overlapping events and improve performance
  cy.one('zoom', () => {
    updateLabelsPositions(cy);
    setZoomCy(cy.zoom());
  });

  cy.on('mouseover', 'node', async evt => {
    const node = evt.target;
    node.grabify();

    const isParent = checkNodeType({ node, typeToCheck: props.type.parent });

    if (!isParent) {
      setHoverNode(node.data());

      if (tooltipNodeEle) {
        popperRef = node.popper({
          content: tooltipNodeEle,
          popper: {
            placement: 'auto-start',
          },
        });
        if (popperRef) {
          await popperRef.update();
          tooltipNodeEle.style.display = 'block';
        }
      }
    }
  });

  cy.on('mouseout', 'node', evt => {
    const node = evt.target;
    if (tooltipNodeEle) {
      const isParent = checkNodeType({
        node,
        typeToCheck: props.type.parent,
      });

      if (!isParent) tooltipNodeEle.style.display = 'none';
    }
  });
};

export default dialogGraphEvents;
